import React, { useMemo, useCallback } from "react";
import { Box, VStack } from "@chakra-ui/react";
import { QrCode } from "lucide-react";
import { 
  useGetQrCodesQuery,
  useQrCodeFilters,
  useQrCodeActions,
  QrCodeDetailsModal,
  createQrCodeColumns,
  createQrCodeActions,
} from "@entities/qr-codes";
import { QrCodeCreateModal, QrCodeEditModal } from "@entities/qr-codes/components/QrCodeCreateEditModal";
import EntityTableSection from "@/components/section/EntityTableSection";
import PageHeader from "@/components/ui/PageHeader";
import SearchAndFiltersSection from "@/components/section/SearchAndFiltersSection";
import ErrorAlert from "@/components/ErrorAlert";
import LoadingSkeleton from "@/components/LoadingSkeleton";
import ConfirmationModal from "@/components/modals/ConfirmationModal";
import Pagination from "@/components/ui/Pagination";

const QrCodesPage: React.FC = () => {
  // Performance optimized with memoized hooks and callbacks
  const { filters, queryParams, handleFilterChange } = useQrCodeFilters();
  const actions = useQrCodeActions();

  const {
    data: qrCodesResponse,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useGetQrCodesQuery(queryParams, {
    refetchOnMountOrArgChange: false, // Don't refetch when component re-renders due to modal state changes
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  // Extract QR codes array from paginated response
  const qrCodesArray = qrCodesResponse?.data || [];
  const totalQrCodes = qrCodesResponse?.total || 0;

  // Memoize search filters configuration
  const searchFilters = useMemo(
    () => [],
    []
  );

  // Memoize table columns to prevent recreation
  const tableColumns = useMemo(() => createQrCodeColumns(), []);

  // Memoize table actions
  const tableActions = useMemo(
    () => createQrCodeActions(actions.handleAction, actions.isDeleting),
    [actions.handleAction, actions.isDeleting]
  );

  // Memoize delete confirmation handler
  const handleDeleteConfirm = useCallback(async () => {
    const success = await actions.handleDelete();
    if (success) {
      refetch();
    }
  }, [actions.handleDelete, refetch]);

  // Memoize search change handler to prevent inline function re-renders
  const handleSearchChange = useCallback(
    (value: string) => {
      handleFilterChange("search", value);
    },
    [handleFilterChange]
  );

  // Memoize modal success handler to refresh data after create/update
  const handleModalSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Memoize page change handler
  const handlePageChange = useCallback((page: number) => {
    handleFilterChange('page', page);
  }, [handleFilterChange]);

  // Memoize page size change handler
  const handlePageSizeChange = useCallback((pageSize: number) => {
    handleFilterChange('limit', pageSize);
  }, [handleFilterChange]);

  if (isLoading && !qrCodesArray.length) {
    return <LoadingSkeleton rows={5} />;
  }

  if (isError) {
    return (
      <ErrorAlert
        isError={true}
        title="Failed to load QR codes"
        message={error?.toString() || "An error occurred while loading data"}
        onRetry={refetch}
      />
    );
  }

  return (
    <Box maxW="full" mx="auto" pb={8}>
      <VStack gap={6} align="stretch">
        <PageHeader
          title="QR Code Pixel Shares Management"
          subtitle={`${totalQrCodes} total QR code pixel shares`}
          onAddNew={actions.handleCreateNew}
          addButtonText="Create QR Code Pixel Share"
          onRefresh={refetch}
          isRefreshing={isFetching}
        />

        <SearchAndFiltersSection
          searchValue={filters.search}
          searchPlaceholder="Search by QR code pixel share name..."
          onSearchChange={handleSearchChange}
          filters={searchFilters}
        />

        <EntityTableSection
          data={qrCodesArray}
          columns={tableColumns}
          actions={tableActions}
          isLoading={isFetching}
          onRowClick={actions.handleRowClick}
          emptyState={{
            icon: QrCode,
            title: "No QR codes found",
            description: "Create your first QR code to get started",
          }}
        />

        {/* Pagination */}
        {qrCodesResponse && (
          <Pagination
            paginationInfo={{
              current_page: qrCodesResponse.current_page,
              last_page: qrCodesResponse.last_page,
              per_page: qrCodesResponse.per_page,
              total: qrCodesResponse.total,
            }}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSizeSelector={true}
            isLoading={isFetching}
          />
        )}

        {/* Hash-managed Modals - Each modal self-determines if it should render */}
        <QrCodeDetailsModal />

        <QrCodeCreateModal
          onSuccess={handleModalSuccess}
        />

        <QrCodeEditModal
          onSuccess={handleModalSuccess}
        />

        {actions.deleteConfirmModalProps.isOpen && (
          <ConfirmationModal
            {...actions.deleteConfirmModalProps}
            onConfirm={handleDeleteConfirm}
          />
        )}
      </VStack>
    </Box>
  );
};

export default QrCodesPage; 